function normalizeForNameMatch(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
}

function looksLikeNameLine(line: string, candidateName: string): boolean {
  const a = normalizeForNameMatch(line);
  const b = normalizeForNameMatch(candidateName);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length >= 4 && b.length >= 4 && (a.includes(b) || b.includes(a))) return true;
  const aTokens = new Set(a.split(" ").filter((t) => t.length >= 2));
  const bTokens = new Set(b.split(" ").filter((t) => t.length >= 2));
  if (aTokens.size === 0 || bTokens.size === 0) return false;
  const shared = [...aTokens].filter((t) => bTokens.has(t)).length;
  return shared >= Math.min(2, Math.min(aTokens.size, bTokens.size));
}

function isContactInfoLine(line: string): boolean {
  const t = line.trim();
  if (!t) return false;
  if (/^(phone|mobile|cell(?:ular)?|tel(?:ephone)?|fax|contact\s*(?:no\.?|num(?:ber)?)?)\s*[:：]/i.test(t)) return true;
  if (/^(e-?mail)\s*[:：]/i.test(t)) return true;
  if (/[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/.test(t)) return true;
  if (/^(linkedin|twitter|facebook|instagram|github|skype|portfolio|website|web)\s*[:：]?\s*https?:\/\//i.test(t)) return true;
  if (/^https?:\/\/(www\.)?linkedin\.com\//i.test(t)) return true;
  if (/^(www\.)?linkedin\.com\//i.test(t)) return true;
  if (/^\+?[\d\s\-–—().+]{8,}$/.test(t) && (t.match(/\d/g) ?? []).length >= 7) return true;
  if (/,\s*(?:philippines?|metro\s+manila|ncr|luzon|visayas|mindanao|singapore|malaysia|indonesia|australia|canada|usa|u\.s\.a\.|united\s+states?|united\s+kingdom|u\.k\.|new\s+zealand|hong\s+kong|uae|dubai|japan|korea)\s*$/i.test(t) && t.length <= 80 && !t.includes(":")) return true;
  return false;
}

const SIDEBAR_START_RE = /^(skills?\s*(?:summary)?|core\s+competenc(?:y|ies)|technical\s+skills?|key\s+skills?|character\s+references?|references?|certifications?(?:\s+and\s+(?:awards?|honors?|recognition))?|awards?(?:\s+and\s+(?:certifications?|honors?|recognition))?|hobbies?\s*(?:and\s+interests?)?|achievements?|accomplishments?)\s*:?\s*$/i;

function reorderColumns(text: string): string {
  let result = text.replace(
    /\[COLUMN A\]\n([\s\S]*?)\n\n\[COLUMN B\]\n([\s\S]*?)(?=\n\n\[COLUMN A\]|$)/g,
    (_m, colA, colB) => {
      const firstLineA = colA.split("\n").find((l: string) => l.trim())?.trim() ?? "";
      return SIDEBAR_START_RE.test(firstLineA) ? colB.trim() + "\n\n" + colA.trim() : colA.trim() + "\n\n" + colB.trim();
    }
  );
  return result.replace(/^\[COLUMN [AB]\]\n?/gm, "");
}

function deferSidebarBlocks(text: string): string {
  const jRe = /^.{2,}\s+\b(?:19|20)\d{2}\s*(?:[-–—]|\s+to)\s*(?:(?:19|20)\d{2}|present|current|now)\b/i;
  const isSecHeader = (s: string) => s.length >= 3 && s.length <= 60 && s === s.toUpperCase() && /[A-Z]/.test(s) && !/\d/.test(s);
  const tlines = text.split("\n");
  const out: string[] = [];
  let idx = 0;

  while (idx < tlines.length) {
    const t = tlines[idx].trim();
    if (!SIDEBAR_START_RE.test(t)) { out.push(tlines[idx]); idx++; continue; }

    const sb: string[] = [tlines[idx]];
    let j = idx + 1;
    while (j < tlines.length) {
      const nt = tlines[j].trim();
      if (!nt) { j++; break; }
      if (nt.length > 60 || jRe.test(nt) || SIDEBAR_START_RE.test(nt) || isSecHeader(nt)) break;
      sb.push(tlines[j]);
      j++;
    }

    let hasBody = false;
    for (let k = j; k < Math.min(j + 50, tlines.length); k++) {
      const nt = tlines[k].trim();
      if (!nt) continue;
      if (isSecHeader(nt) || SIDEBAR_START_RE.test(nt)) break;
      if (nt.length > 80 || jRe.test(nt)) { hasBody = true; break; }
    }

    if (hasBody) {
      const body: string[] = [];
      let k = j;
      while (k < tlines.length) {
        const nt = tlines[k].trim();
        if (SIDEBAR_START_RE.test(nt) || isSecHeader(nt)) break;
        body.push(tlines[k]);
        k++;
      }
      while (body.length > 0 && !body[body.length - 1].trim()) body.pop();
      out.push(...body, "", ...sb);
      idx = k;
    } else {
      out.push(...sb);
      idx = j;
    }
  }
  return out.join("\n");
}

const DATE_RANGE_RE = /^(?:(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+)?(?:19|20)\d{2}\s*(?:[-–—]|\s+to)\s*(?:(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+)?(?:(?:19|20)\d{2}|present|current|now)\b/i;
const DESC_VERB_RE = /\b(Lead|Manage|Develop|Oversee|Drive|Handle|Direct|Coordinate|Build|Responsible\s+for|Head|Create|Launch|Oversaw|Drove|Execute|Design|Establish|Implement|Deliver|Support|Maintain|Ensure|Collaborate|Supervise|Monitor|Analyze|Serve|Assist|Provide|Spearhead|Streamline|Facilitate|Conduct|Perform|Resolve|Administer|Prepare|Review|Evaluate|Train|Coach|Mentor)(s|ed|ing)?\b/;

function splitMergedTitleDesc(text: string): string {
  const tvLines = text.split("\n");
  const tvOut: string[] = [];
  let linesAfterDate = 0;

  for (const line of tvLines) {
    const t = line.trim();
    if (DATE_RANGE_RE.test(t)) { linesAfterDate = 4; tvOut.push(line); continue; }
    if (linesAfterDate > 0 && t && !t.startsWith("-") && !t.startsWith("•")) {
      linesAfterDate--;
      const vm = t.match(DESC_VERB_RE);
      if (vm) {
        const vIdx = t.indexOf(vm[0]);
        const titlePart = t.slice(0, vIdx).trim();
        const descPart = t.slice(vIdx).trim();
        if (titlePart.length > 0 && titlePart.length <= 60 && !/[.!?]/.test(titlePart)) {
          tvOut.push(titlePart, descPart);
          continue;
        }
      }
    } else if (!t) { linesAfterDate = 0; }
    tvOut.push(line);
  }
  return tvOut.join("\n");
}

function stripContactLines(text: string, candidateName: string): string {
  text = text.replace(/[☎✆\u{1F4DE}\u{1F4DF}\u{1F4F1}\u{1F4F2}\u{1F57B}\u{1F580}\u{1F581}]\s*[:\-]?\s*/gu, "Phone: ");
  text = text.replace(/[✉\u{1F4E7}\u{1F4E8}\u{1F4E9}\u{1F4E4}\u{1F4E5}\u{1F582}\u{1F583}\u{1F584}\u{1F585}]\s*[:\-]?\s*/gu, "Email: ");
  text = text.replace(/[-]/g, "");

  const lines = text.split("\n");
  const result: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (isContactInfoLine(trimmed)) continue;
    if (/^(Phone|Email):\s*$/.test(trimmed)) {
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      if (j < lines.length) { result.push(`${trimmed} ${lines[j].trim()}`); i = j; continue; }
    }
    if (/^\s*(contact\s+address|contract\s+address|current\s+address|home\s+address|mailing\s+address|address|residence|location)\s*:/i.test(line)) {
      const collected = [trimmed.replace(/contract\s+address/i, "Contact Address")];
      let j = i + 1;
      while (j < lines.length) {
        const nt = lines[j].trim();
        if (!nt) break;
        if (/^(phone|email|tel|mobile|cell|fax|web|website|skype|linkedin|twitter|instagram|facebook|github|name|age|gender|nationality|date\s+of\s+birth|civil\s+status|religion)\s*:/i.test(nt)) break;
        if (nt === nt.toUpperCase() && /[A-Z]/.test(nt) && nt.length <= 60) break;
        collected.push(nt);
        j++;
      }
      result.push(collected.join(", "));
      i = j - 1;
      continue;
    }
    result.push(line);
  }

  let cleaned = result.join("\n");
  if (!candidateName?.trim()) return cleaned;

  const outLines = cleaned.split("\n");
  let i = 0;
  while (i < outLines.length && !outLines[i].trim()) i++;
  if (i < outLines.length && looksLikeNameLine(outLines[i], candidateName)) {
    outLines.splice(i, 1);
    while (i < outLines.length && !outLines[i].trim()) outLines.splice(i, 1);
  }
  return outLines.join("\n");
}

export function preprocessResumeText(raw: string, candidateName: string): string {
  if (!raw) return "";
  let text = raw.replace(/\r\n/g, "\n");
  text = reorderColumns(text);
  text = deferSidebarBlocks(text);
  text = text.replace(/^(other\s+(?:work\s+)?experiences?|additional\s+(?:work\s+)?experiences?)\s+(.+)$/gim, "$1\n$2");
  text = splitMergedTitleDesc(text);
  text = stripContactLines(text, candidateName);
  return text;
}
